import React, { useState, useEffect, useRef } from "react";
import {
  deleteReminder,
  getReminderAccordingtoDate,
  sendBulkReminder,
} from "./services/services";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const AllReminders = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [selectedClinic, setSelectedClinic] = useState("");
  const [selectedPlace, setSelectedPlace] = useState("");
  const [selected, setSelected] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const headerCheckboxRef = useRef(null);

  const formatDate = (input) => {
    if (!input) return "-";
    const parsed = Date.parse(input);
    if (!isNaN(parsed)) {
      const d = new Date(parsed);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    try {
      const d2 = new Date(input);
      if (!isNaN(d2)) {
        const dd = String(d2.getDate()).padStart(2, "0");
        const mm = String(d2.getMonth() + 1).padStart(2, "0");
        const yyyy = d2.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      }
    } catch {}
    return String(input);
  };

  useEffect(() => {
    const fetchReminders = async () => {
      setLoading(true);
      try {
        const payload = {
          data: { filter: "" },
          page: 0,
          pageSize: 10000,
          order: [["date", "ASC"]],
        };
        const res = await getReminderAccordingtoDate(payload);

        const rows = res?.data?.data?.rows || [];

        const parseTimestamp = (item) => {
          const candidate = item?.date ?? item?.createdAt ?? "";
          const t = Date.parse(candidate);
          return isNaN(t) ? Infinity : t;
        };

        rows.sort((a, b) => parseTimestamp(a) - parseTimestamp(b));

        setReminders(rows);
      } catch (err) {
        console.error("Error fetching reminders:", err);
        toast.error("❌ Failed to load reminders");
      } finally {
        setLoading(false);
      }
    };
    fetchReminders();
  }, []);

  const filtered = reminders.filter((r) => {
    const matchesSearch =
      r.patientName?.toLowerCase().includes(search.toLowerCase()) ||
      r.patientNumber?.includes(search);

    const matchesReason = selectedReason ? r.reason === selectedReason : true;

    const matchesClinic = selectedClinic
      ? r.fromWhere?.toLowerCase() === selectedClinic.toLowerCase()
      : true;

    const matchesPlace = selectedPlace
      ? r.address?.toLowerCase().includes(selectedPlace.toLowerCase())
      : true;

    // 🔹 Normalize record date to YYYY-MM-DD (fallback to createdAt)
    const dateStr = (() => {
      const raw = r.date || r.createdAt;
      if (!raw) return "";
      const d = new Date(raw);
      if (isNaN(d)) return "";
      return d.toISOString().split("T")[0]; // 'YYYY-MM-DD'
    })();

    const matchesDateRange =
      (!fromDate || (dateStr && dateStr >= fromDate)) &&
      (!toDate || (dateStr && dateStr <= toDate));

    return (
      matchesSearch &&
      matchesReason &&
      matchesClinic &&
      matchesPlace &&
      matchesDateRange
    );
  });

  useEffect(() => {
    const visibleIds = filtered.map((r) => r.id);
    const selectedVisibleCount = selected.filter((id) =>
      visibleIds.includes(id)
    ).length;

    if (!headerCheckboxRef.current) return;
    if (selectedVisibleCount === 0) {
      headerCheckboxRef.current.indeterminate = false;
      headerCheckboxRef.current.checked = false;
    } else if (
      selectedVisibleCount === visibleIds.length &&
      visibleIds.length > 0
    ) {
      headerCheckboxRef.current.indeterminate = false;
      headerCheckboxRef.current.checked = true;
    } else {
      headerCheckboxRef.current.indeterminate = true;
      headerCheckboxRef.current.checked = false;
    }
  }, [filtered, selected]);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filtered.map((r) => r.id);
    const allVisibleSelected = visibleIds.every((id) => selected.includes(id));

    if (allVisibleSelected) {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const ids = reminders
        .filter((r) => selected.includes(r.id))
        .map((r) => r.id);
      if (!ids.length) {
        toast.warning("⚠️ Please select at least one reminder");
        setSending(false);
        return;
      }
      const res = await sendBulkReminder(ids);

      if (res?.data?.data) {
        toast.success(
          `✅ Sent: ${res?.data?.data?.results?.sent}, Failed: ${
            res?.data?.data?.results?.failed || 0
          } (Total: ${res?.data?.data?.results?.total})`
        );
      }
      setSelected([]);
    } catch (err) {
      toast.error("❌ Failed to send reminders");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleRowDeleteClick = (id) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    const id = deletingId;
    if (!id) {
      toast.warning("⚠️ Nothing selected to delete");
      setShowDeleteConfirm(false);
      return;
    }

    setDeleting(true);
    try {
      if (typeof deleteReminder === "function") {
        await deleteReminder(id);
      }

      setReminders((prev) => prev.filter((r) => r.id !== id));
      setSelected((prev) => prev.filter((sId) => sId !== id));

      toast.success(`✅ Deleted reminder (ID: ${id})`);
      setShowDeleteConfirm(false);
      setDeletingId(null);
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("❌ Failed to delete reminder");
    } finally {
      setDeleting(false);
    }
  };

  const exportToExcel = () => {
    const dataForExcel = filtered.map((r) => {
      const visits = Array.isArray(r.nextVisits) ? r.nextVisits : [];
      const nextVisits = visits.length > 0 ? visits.join(", ") : "-";
      return {
        ID: r.id,
        Name: r.patientName,
        Phone: r.patientNumber,
        Age: r.Age,
        Address: r.address,
        "Disease / Concern": r.disease,
        From: r.fromWhere,
        Reason: r.reason,
        Date: formatDate(r.date ?? r.createdAt),
        "Next Visits": nextVisits,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reminders");
    XLSX.writeFile(workbook, "reminders.xlsx");
  };

  const renderDateRangeLabel = () => {
    if (!fromDate && !toDate) {
      return <span>Showing all reminders</span>;
    }

    return (
      <>
        Showing reminders
        {fromDate && (
          <>
            {" "}
            from <strong>{fromDate}</strong>
          </>
        )}
        {toDate && (
          <>
            {" "}
            to <strong>{toDate}</strong>
          </>
        )}
      </>
    );
  };

  return (
    <div className="container-fluid py-5">
      <div className="card shadow-lg border-0 rounded-4 w-100">
        <div className="card-body">
          {/* 🔹 Sticky filter bar on desktop */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3 filters-sticky">
            {/* Left side: title + date range label */}
            <div>
              <h2 className="h4 fw-bold text-dark mb-1">
                📋 Patient Reminders
              </h2>
              <p className="text-muted small mb-0">
                {renderDateRangeLabel()}
              </p>
            </div>

            {/* Right side: filters */}
            <div className="d-flex gap-2 flex-wrap">
              {/* From / To date range */}
              <div className="d-flex flex-column flex-lg-row gap-2">
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <select
                className="form-select"
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
              >
                <option value="">All Reasons</option>
                <option value="operation">Operation</option>
                <option value="opd">OPD</option>
                <option value="revisit">Revisit</option>
                 <option value="surgerypending">Surgery Pending</option>
              </select>

              <select
                className="form-select"
                value={selectedClinic}
                onChange={(e) => setSelectedClinic(e.target.value)}
              >
                <option value="">All Clinics</option>
                <option value="clinic">Clinic</option>
                <option value="sum">SUM</option>
                <option value="kims">KIMS</option>
              </select>

              <input
                type="text"
                placeholder="🏥 Search by place"
                value={selectedPlace}
                onChange={(e) => setSelectedPlace(e.target.value)}
                className="form-control"
              />

              <input
                type="text"
                placeholder="🔍 Search name or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-control"
              />

              <button className="btn btn-primary" onClick={exportToExcel}>
                📥 Export to Excel
              </button>
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div
                className="spinner-border text-primary"
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              ></div>
            </div>
          ) : (
            <div className="table-responsive reminders-table-wrapper">
              <table
                className="table table-hover align-middle"
                style={{ tableLayout: "auto" }}
              >
                <thead className="table-primary">
                  <tr>
                    <th className="text-center">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        className="form-check-input"
                        onChange={toggleSelectAllVisible}
                      />
                    </th>

                    <th style={{ minWidth: 60 }}>ID</th>
                    <th style={{ minWidth: 160 }}>Name</th>
                    <th style={{ minWidth: 120 }}>Phone</th>
                    <th style={{ minWidth: 60 }}>Age</th>
                    <th style={{ minWidth: 180 }}>Address</th>
                    <th style={{ minWidth: 140 }}>Disease / Concern</th>
                    <th style={{ minWidth: 100 }}>From</th>
                    <th style={{ minWidth: 110 }}>Reason</th>

                    <th style={{ minWidth: 130, whiteSpace: "nowrap" }}>
                      Date
                    </th>

                    <th style={{ minWidth: 180 }}>Next Visits</th>

                    <th style={{ minWidth: 120 }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((r) => {
                      const visits = (() => {
                        if (Array.isArray(r.nextVisits)) return r.nextVisits;
                        if (typeof r.nextVisits === "string") {
                          try {
                            return JSON.parse(r.nextVisits);
                          } catch {
                            return [];
                          }
                        }
                        return [];
                      })();

                      return (
                        <tr key={r.id}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selected.includes(r.id)}
                              onChange={() => toggleSelect(r.id)}
                            />
                          </td>

                          <td>{r.id}</td>
                          <td>{r.patientName}</td>
                          <td>{r.patientNumber}</td>
                          <td>{r.Age}</td>
                          <td>{r.address}</td>
                          <td>{r.disease}</td>
                          <td>{r.fromWhere}</td>
                          <td>
                            <span className="badge bg-info text-dark">
                              {r.reason}
                            </span>
                          </td>

                          <td style={{ whiteSpace: "nowrap" }}>
                            {formatDate(r.date ?? r.createdAt)}
                          </td>

                          <td>
                            {visits.length > 0
                              ? visits.map((d, idx) => (
                                  <span
                                    key={idx}
                                    className="badge bg-info text-dark me-1"
                                  >
                                    {d}
                                  </span>
                                ))
                              : "-"}
                          </td>

                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRowDeleteClick(r.id)}
                            >
                              🗑️ Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="12" className="text-center py-4 text-muted">
                        No reminders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {selected.length > 0 && !loading && (
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                onClick={sendReminders}
                disabled={sending}
                className="btn btn-success d-flex align-items-center gap-2"
              >
                {sending && (
                  <span
                    className="spinner-border spinner-border-sm text-light me-2"
                    role="status"
                  ></span>
                )}
                🚀 {sending ? "Sending..." : `Send Reminder (${selected.length})`}
              </button>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div
            className="bg-white rounded-3 p-4 shadow"
            style={{ width: 420, maxWidth: "95%" }}
            role="dialog"
            aria-modal="true"
          >
            <h5 className="mb-3">🗑️ Confirm Delete</h5>
            <p className="mb-4">
              Are you sure you want to delete reminder with ID{" "}
              <strong>{deletingId}</strong>? This action cannot be undone.
            </p>

            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingId(null);
                }}
                disabled={deleting}
              >
                Cancel
              </button>

              <button
                className="btn btn-danger d-flex align-items-center gap-2"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting && (
                  <span
                    className="spinner-border spinner-border-sm text-light me-2"
                    role="status"
                  />
                )}
                Delete {deleting ? "..." : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllReminders;
